<template>
  <section class="mb-4 create-new-class">
    <iframe-dialog ref="frame" />
    <v-stepper v-model="addStep" vertical>
      <div class="mx-3 mt-3">
        <h2>Add a New Class</h2>
        <p class="ma-0">
          You need to create a new class. Follow the below steps to create a new
          class.
        </p>
      </div>
      <v-stepper-step
        clas="align-start"
        :complete="addStep > 1"
        step="1"
        :color="addStep > 1 ? 'success' : 'primary'"
      >
        {{ addStep > 1 ? 'Class name' : 'Class name' }}
        <h4 v-if="addStep > 1">
          {{ classTitle }}
        </h4>
      </v-stepper-step>

      <v-stepper-content step="1">
        <form data-vv-scope="step-1" @submit.prevent>
          <v-text-field
            key="class-title-input"
            v-model="classTitle"
            v-validate="'required|max:150'"
            label="Class Name"
            hint="Enter the name of your class"
            data-vv-name="title"
            data-vv-as="class name"
            required
          />
        </form>
        <v-btn color="primary" @click="createClass">Continue</v-btn>
      </v-stepper-content>

      <v-stepper-step
        clas="align-start"
        :complete="addStep > 2"
        step="2"
        :color="addStep > 2 ? 'success' : 'primary'"
      >
        {{ addStep > 2 ? 'Year Level' : 'Please select your Year level' }}
        <h4 v-if="addStep > 2">{{ classLevel.value }}</h4>
      </v-stepper-step>

      <v-stepper-content step="2">
        <form data-vv-scope="step-2" @submit.prevent>
          <v-autocomplete
            ref="classLevel"
            name="classLevel"
            v-model="classLevel"
            v-validate="'required|max:150'"
            :error-messages="errors.collect('step-2.classLevel')"
            :rules="[() => !!classLevel || 'This field is required']"
            :items="yearLevels"
            data-vv-as="year level"
            item-text="value"
            item-value="id"
            return-object
            label="Year Level"
            placeholder="Please select year level..."
            required
          />
        </form>
        <v-btn color="primary" @click="chooseYearLevel">Continue</v-btn>
        <v-btn text @click="addStep--">Back</v-btn>
      </v-stepper-content>

      <v-stepper-step
        clas="align-start"
        :complete="addStep > 3"
        step="3"
        :color="addStep > 3 ? 'success' : 'primary'"
      >
        {{
          addStep > 3
            ? `${capitalizeFirstLetter(lessonTerm)}s`
            : `Please select ${lessonTerm}s for your class`
        }}
        <div v-if="addStep > 3">
          <v-chip
            v-for="(lesson, index) in selectedLessons"
            :key="index"
            class="mr-2"
            label
            outlined
            disabled
            color="grey"
          >
            {{ lesson.lesson.name }}
          </v-chip>
        </div>
      </v-stepper-step>

      <v-stepper-content step="3">
        <v-row class="mb-2">
          <v-col
            v-for="(lesson, index) in activeLessons"
            :key="index"
            lg="4"
            md="6"
            sm="12"
            cols="12"
          >
            <lesson-card ref="lessons" :lesson="lesson"></lesson-card>
          </v-col>
        </v-row>
        <p>
          Please note: You can select a new {{ lessonTerm }}s after after
          creating your class
        </p>
        <v-btn color="primary" @click="chooseLesson">Continue</v-btn>
        <v-btn text @click="addStep--, (hasLesson = true)">Back</v-btn>
        <p v-show="!hasLesson" class="animate bounceIn" style="color: red">
          Please select at least one {{ lessonTerm }} for your class
        </p>
      </v-stepper-content>

      <v-stepper-step
        id="new-class-announcement"
        ref="announcement"
        :complete="addStep > 4"
        step="4"
      >
        {{
          !classAnnouncement
            ? 'Make an announcement to your class'
            : 'Announcement for your class'
        }}
      </v-stepper-step>
      <v-stepper-content step="4">
        <form data-vv-scope="step-4" class="mb-2" @submit.prevent>
          <v-text-field
            label="Title"
            v-model="classAnnouncementTitle"
            v-validate="'required'"
            data-vv-name="title"
            data-vv-as="title"
            :error-messages="errors.collect('step-4.title')"
          ></v-text-field>

          <v-textarea
            key="announcement-textarea"
            v-model="classAnnouncement"
            v-validate="'required|max:150'"
            :error-messages="errors.collect('step-4.announcement')"
            label="Announcement"
            hint="Enter an announcement to your class"
            data-vv-name="announcement"
            data-vv-as="announcement"
            required
          ></v-textarea>
        </form>
        <v-btn
          color="primary"
          @click="submit"
          :disabled="loading"
          :loading="loading"
        >
          Create Class
        </v-btn>
        <v-btn text @click="addStep--">Back</v-btn>
      </v-stepper-content>
    </v-stepper>
  </section>
</template>

<script>
import LessonCard from '@/modules/lesson/views/LessonCard'
import IframeDialog from './IframeDialog'
import Class from '@/modules/class/models/Class'
import Lesson from '@/modules/lesson/models/Lesson'
import YearLevel from '@/modules/lesson/models/YearLevel'
import { setTimeout } from 'timers'
import { mapState } from 'vuex'

export default {
  name: 'CreateNew',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    LessonCard,
    IframeDialog
  },
  data() {
    return {
      addStep: 1,
      classTitle: null,
      classLevel: [],
      classLesson: [],
      classAnnouncement: '',
      classAnnouncementTitle: '',
      visible: false,
      password: '',
      loading: false,
      hasLesson: true
    }
  },
  computed: {
    ...mapState(['lessonTerm']),
    activeLessons() {
      return Lesson.all()
    },
    yearLevels() {
      return YearLevel.all()
    },
    selectedLessons() {
      if (!this.$refs.lessons) return []
      return this.$refs.lessons.filter(x => x.isSelected)
    }
  },
  async mounted() {
    await YearLevel.dispatch('fetch')
    await Lesson.dispatch('fetch', this.$route.meta.type)
  },
  methods: {
    async chooseLesson() {
      if (!this.$refs.lessons.some(val => val.isSelected)) {
        this.hasLesson = false
      } else {
        this.hasLesson = true
        this.addStep++
        setTimeout(() => {
          this.$scrollTo(this.$refs.announcement.$el, 500, { offset: -80 })
        }, 750)
      }
    },
    async chooseYearLevel() {
      let result = await this.$validator.validateAll('step-2')

      if (!result) return

      this.addStep++
    },
    async createClass() {
      let result = await this.$validator.validateAll('step-1')

      if (!result) return

      this.addStep++
    },
    async submit() {
      let result = await this.$validator.validateAll('step-4')

      if (!result) return
      this.loading = true
      const selectedLessonsId = this.selectedLessons.map(x => x.id)

      let payload = {
        name: this.classTitle,
        yearLevels: this.classLevel.id,
        classMessage: this.classAnnouncement,
        classMessageTitle: this.classAnnouncementTitle,
        selectedLessons: selectedLessonsId,
        module: 'teacher'
      }

      const customClass = await Class.dispatch('create', { payload: payload })
      this.loading = false
      this.$emit('classCreated', customClass)
      this.reset()
    },
    reset() {
      this.classTitle = null
      this.classLevel = []
      this.classAnnouncement = null
      this.classAnnouncementTitle = null
      this.$refs.lessons.map(x => (x.isSelected = false))
      this.addStep = 1
    }
  }
}
</script>

<style lang="scss" scoped>
.create-new-class {
  .abs {
    position: absolute;
    top: 50%;
  }
}
</style>
